
import SockJS from "sockjs-client";
import { over } from "stompjs";

import { useEffect, useRef } from "react";
import { getAccessToken } from "src/utils/authentication.service";
import { useQueryClient } from "@tanstack/react-query";

const baseUrl = process.env.REACT_APP_WEBSOCKET_URL;

/**
 * Custom React hook for subscribing to a WebSocket topic
 * and automatically updating React Query data when messages arrive.
 *
 * @param queryKey - React Query key to invalidate/refetch on new data
 * @param endpoint - WebSocket topic endpoint (e.g., '/topic/updates')
 * @param parseMessage - Function to parse incoming message payloads
 */


export const useWebSocketSubscription = <TData>(
  endpoint: string,
  parseMessage: (body: any) => { queryKey: any[]; data: TData | null } | null,
  onComplete?: (payload?: TData) => void
) => {
  // React Query's queryClient for cache invalidation
  const queryClient = useQueryClient();
  const clientRef = useRef<any | null>(null);
  const subRef = useRef<any | null>(null);
  const token = getAccessToken();

  useEffect(() => {
  // Initialize SockJS client with backend WebSocket base URL

    if (!baseUrl) return;

    const sock = new SockJS(`${baseUrl}/ws?access_token=${token}`);
    const client = over(sock);

    // Outgoing heartbeat: the client will send a "ping" to the server every 20 seconds (20000 ms)
    client.heartbeat.outgoing = 20000;

    // Incoming heartbeat: set to 0, meaning the client does not expect periodic "ping" messages from the server
    client.heartbeat.incoming = 0;

    clientRef.current = client;

    const onConnect = () => {
    // Subscribe to the specified endpoint/topic
      subRef.current = client.subscribe(endpoint, (message: any) => {
        if (!message?.body) return;

        try {
          const parsed = parseMessage(JSON.parse(message.body));
          if (!parsed || !parsed.data) return;

          const { queryKey, data } = parsed;

          // Update React Query cache with new data
          queryClient.setQueryData(queryKey, (old: any) => ({ ...(old || {}), ...(data as any) }));
          onComplete?.(data);
        } catch (err) {
          console.error("Failed to parse message:", err);
        }
      });
    };

    const connect = () => {
      client.connect({}, onConnect, (err) => { 
        // Retry connection on error after a delay
        console.error("STOMP connect error:", err);
        setTimeout(connect, 5000);
      }
      );
    };

    sock.onclose = () => {
      console.warn("WebSocket disconnected. Reconnecting...");
      setTimeout(connect, 5000);
    };
    
    connect();

    // Cleanup on unmount
    return () => {
      subRef.current?.unsubscribe();
      if (clientRef.current?.connected) {
        clientRef.current.disconnect(() => console.log("STOMP disconnected safely"));
      }
      clientRef.current = null;
      subRef.current = null;
    };
  }, [endpoint, parseMessage, queryClient, onComplete]);
};